/**
 * 購物車資料遷移工具（cartDataConverter）
 *
 * ─────────────────────────────────────────────────────────────
 * 為什麼需要這個檔案？
 * ─────────────────────────────────────────────────────────────
 * 金牌客製化的文字欄位格式歷經兩波異動，舊資料可能仍以舊格式存放在
 * 使用者 localStorage 的購物車（cartorder）中。
 * 此工具在 App.vue created() 中每次啟動時執行，將購物車資料統一遷移
 * 至最新格式後寫回（冪等：已遷移的資料安全通過，不受影響）。
 *
 * ─────────────────────────────────────────────────────────────
 * 兩波異動說明
 * ─────────────────────────────────────────────────────────────
 * 【第一波 — 2025-10-17】
 *   文字欄位從扁平的 `text` 物件改為結構化的 `engraving_content`：
 *     舊格式：{ ..., text: { templeName, godName, gift, respect } }
 *     新格式：{ ..., engraving_content: { type, content } }
 *   此時 engraving_content.type 只有兩種值：
 *     - "default"     → 一般金牌
 *     - "fan"         → 法扇（當時 shape 稱為 "扇子"）
 *
 * 【第二波 — 術語更名（黃金法扇改名神明法器）】
 *   - item.shape：          "扇子" → "法器"
 *   - engraving_content.type："fan"  → "sacredObject"
 *
 * ─────────────────────────────────────────────────────────────
 * 三個函式職責
 * ─────────────────────────────────────────────────────────────
 * inferEngravingTypeFromItem(item)
 *   針對第一波前的超舊格式（只有 text 欄位）推斷 engraving type。
 *   邏輯：shape === "扇子" → "sacredObject"，其餘 → "default"。
 *
 * migrateTerminology(item)
 *   執行第二波術語遷移，可安全地對任何格式的資料呼叫：
 *   shape "扇子" → "法器"、engraving_content.type "fan" → "sacredObject"。
 *
 * convertLegacyCartData(cartorder)
 *   主函式，對購物車陣列中每一筆資料依以下三種情境處理：
 *
 *   情境 A：已有 engraving_content（第一波後的資料）
 *     → 只需套用術語遷移（處理第一波留下的舊術語 "fan"/"扇子"）
 *
 *   情境 B：只有 text 欄位（第一波前的超舊格式）
 *     → 推斷 type → 移除 text → 建立 engraving_content
 *     → sacredObject：content 設為 {}（法器不需文字欄位）
 *     → default：content 直接沿用舊 text 物件（欄位結構相同）
 *     → shape 名稱一併遷移
 *
 *   情境 C：兩者皆無（不明格式）
 *     → 安全套用術語遷移後回傳
 * ─────────────────────────────────────────────────────────────
 */

/**
 * 從商品資訊推斷 engravingContentType
 * 20251017 前只會有 sacredObject 與 default 兩種
 * @param {Object} item - 購物車商品項目
 * @returns {string} - engraving content type
 */
function inferEngravingTypeFromItem(item) {
  // 法器類型（舊術語 "扇子"）
  if (item.shape === "扇子") {
    return "sacredObject";
  }

  // 預設
  return "default";
}

/**
 * 將商品資料中的舊術語遷移至新術語（第二波遷移）
 * - shape: "扇子" → "法器"
 * - engraving_content.type: "fan" → "sacredObject"
 * @param {Object} item - 購物車商品項目
 * @returns {Object} - 遷移後的商品項目
 */
function migrateTerminology(item) {
  let result = item;

  if (result.shape === "扇子") {
    result = { ...result, shape: "法器" };
  }

  if (result.engraving_content && result.engraving_content.type === "fan") {
    result = {
      ...result,
      engraving_content: { ...result.engraving_content, type: "sacredObject" },
    };
  }

  return result;
}

/**
 * 檢查並轉換舊格式購物車資料為新格式
 * @param {Array} cartorder - 購物車商品陣列
 * @returns {Array} - 轉換後的購物車商品陣列
 */
export function convertLegacyCartData(cartorder) {
  if (!Array.isArray(cartorder)) {
    return cartorder;
  }

  return cartorder.map((item) => {
    // 如果已是新格式，仍需套用術語遷移（處理第一波留下的舊術語）
    if (item.engraving_content) {
      return migrateTerminology(item);
    }

    // 如果是舊格式（有 text 欄位），轉換為新格式
    if (item.text) {
      const type = inferEngravingTypeFromItem(item);
      const { text, ...rest } = item;
      const content = type === "sacredObject" ? {} : text;
      const migratedShape = rest.shape === "扇子" ? "法器" : rest.shape;

      return {
        ...rest,
        shape: migratedShape,
        engraving_content: {
          type: type,
          content: content,
        },
      };
    }

    // 無法識別的格式，套用術語遷移後返回
    return migrateTerminology(item);
  });
}
